import {
  Box,
  capitalize,
  Card,
  CardContent,
  CardHeader,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {
  type RestoreAccount,
  type SourceAccount,
  CloudAccountType,
  type Account,
} from '@repo/api-gw-sdk';
import { useEffect, useState } from 'react';

import { Loader } from '@/components/layout/loading';
import { PanelFooter } from '@/components/panels/panelFooter';
import { AllRegionsSelector } from '@/components/selectors/allRegionsSelector';
import type { useDAL } from '@/contexts/useDAL';
import { getAccountProvider, getAccountType } from '@/utils/accounts';

import { ErrorDialog } from '../dialogs/errorDialog';

export const AccountJobMetrics = ({
  account,
  onClose,
  useMetricConfiguration,
  enableMetrics,
  disableMetrics,
}: {
  account: Account | SourceAccount | RestoreAccount;
  onClose: () => void;
  useMetricConfiguration: () => ReturnType<
    ReturnType<typeof useDAL>['cloudAccounts'][
      | 'restore'
      | 'source']['metrics']['useMetric']
  >;
  enableMetrics: (
    account: Account | SourceAccount | RestoreAccount,
    region: string
  ) => Promise<void>;
  disableMetrics: (
    account: Account | SourceAccount | RestoreAccount
  ) => Promise<void>;
}) => {
  const { data: metric, isLoading } = useMetricConfiguration();
  const [enabled, setEnabled] = useState(false);
  const [regionName, setRegionName] = useState<string | undefined>(undefined);
  const [error, setError] = useState<Error | undefined>();
  const accountType = getAccountType(account);
  const entityDisplayName =
    accountType === CloudAccountType.Source ? 'backup' : 'restore';

  useEffect(() => {
    if (metric) {
      const config =
        'restoreAccountConfig' in metric
          ? metric.restoreAccountConfig
          : metric.sourceAccountConfig;

      setEnabled(config.enabled);
      setRegionName(config.destination.aws?.region ?? undefined);
    }
  }, [metric]);

  const onSave = async () => {
    try {
      if (!enabled) {
        await disableMetrics(account);
      } else {
        if (!regionName) return;
        await enableMetrics(account, regionName);
      }
      onClose();
    } catch (error) {
      setError(error instanceof Error ? error : new Error('Unknown error'));
    }
  };

  return (
    <Stack className='h-full'>
      <Stack
        marginX='100px'
        marginTop='40px'
        overflow='auto'
        flexGrow={1}
        gap='24px'
      >
        <Typography variant='body1'>
          Control which metrics are published to your AWS CloudWatch account to
          power dashboards, alarms, and insights.
        </Typography>
        <JobMetricConfigCard
          isLoading={isLoading}
          description={`Monitor and analyze ${entityDisplayName} job performance, published hourly.`}
          title={`${capitalize(entityDisplayName)} jobs metrics`}
          account={account}
          enabled={enabled}
          setEnabled={setEnabled}
          regionName={regionName}
          setRegionName={setRegionName}
        />
      </Stack>
      <PanelFooter
        primaryButton={{
          text: 'Save changes',
          disabled: enabled && !regionName,
          onClick: onSave,
          'data-testid': 'job-metrics-save-btn',
        }}
        secondaryButton={{
          text: 'Cancel',
          onClick: onClose,
          'data-testid': 'job-metrics-cancel-btn',
        }}
      />
      <ErrorDialog
        isOpen={!!error}
        title='Couldn’t save restore account configuration'
        description='Check the error details and contact support if needed.'
        error={error}
        onClose={() => setError(undefined)}
      />
    </Stack>
  );
};

const JobMetricConfigCard = ({
  isLoading,
  description,
  title,
  account,
  enabled,
  setEnabled,
  regionName,
  setRegionName,
}: {
  isLoading: boolean;
  description: string;
  title: string;
  account: Account | SourceAccount | RestoreAccount;
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  regionName?: string;
  setRegionName: (regionName: string) => void;
}) => {
  const cloudProvider = getAccountProvider(account);
  return isLoading ? (
    <Loader />
  ) : (
    <Card
      sx={(theme) => ({
        maxWidth: '1000px',
        width: '50%',
        background: theme.palette.mode === 'dark' ? undefined : 'white',
        border: '1px solid var(--mui-palette-divider)',
      })}
    >
      <CardHeader
        title={
          <Stack gap='12px'>
            <Stack
              direction='row'
              alignItems='center'
              gap='12px'
              justifyContent='space-between'
            >
              <Typography variant='h4'>{title}</Typography>
              <Switch checked={enabled} onChange={() => setEnabled(!enabled)} />
            </Stack>
            <Typography width='540px' variant='body1'>
              {description}
            </Typography>
          </Stack>
        }
      />
      <CardContent>
        {enabled && (
          <Box sx={{ marginTop: '8px' }} width='340px'>
            <AllRegionsSelector
              title='Publish metrics to'
              provider={cloudProvider}
              regionName={regionName}
              setRegionName={(regionName) => setRegionName(regionName)}
            />
          </Box>
        )}
      </CardContent>
    </Card>
  );
};
